import { ReactElement, useEffect } from 'react';

import { GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { OrganizationJsonLd } from 'next-seo';
import { useRouter } from 'next/router';

import { DefaultLayout, Footer, Header } from '@/components/layouts';
import { DynamicBlockSelector } from '@/components/sections/dynamic-blocks/DynamicBlockSelector';
import { PropsBuilder } from '@/graphql/api';
import { useGetGlobalQuery, useGetHomeQuery, useGetMenuQuery } from '@/graphql/generated';
import { useLocale } from '@/hooks';
import { AvailableLanguages, DEFAULT_LOCALE, LOCALE, LOCALES, PAGE_REVALIDATE_TIME } from '@/utils/constants';
import { getNavigatorLanguage } from '@/utils/helper-functions/lang';

import { NextPageWithLayout } from './_app';

const REDIRECTION_ON_NAVIGATOR_LANG = false;

const Home: NextPageWithLayout = () => {
  const locale = useLocale();
  const router = useRouter();
  const { blocks } = useGetHomeQuery({
    locale,
  }).data?.home?.data?.attributes!;
  const { data: menuData } = useGetMenuQuery();

  useEffect(() => {
    if (window && REDIRECTION_ON_NAVIGATOR_LANG) {
      const navigatorLang = getNavigatorLanguage(window.navigator.language);
      if (Object.values(LOCALE).includes(navigatorLang as AvailableLanguages)) {
        router.replace(navigatorLang);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <OrganizationJsonLd
        type="Organization"
        name="Startup House"
        alternateName={['Startup House – Custom Software Development in Warsaw, Poland', 'SH']}
        url="https://startup-house.com"
        logo="https://startup-house.com/assets/logo.png"
        brand={{
          '@type': 'Brand',
          name: 'SH',
        }}
        sameAs={[
          'https://www.linkedin.com/company/startup-house/',
          'https://www.facebook.com/startuphouse',
          'https://instagram.com/startuphousecom',
        ]}
        address={{
          '@type': 'PostalAddress',
          streetAddress: 'Aleje Jerozolimskie 81',
          addressLocality: 'Warsaw',
          postalCode: '02-001',
          addressCountry: 'PL',
        }}
        contactPoint={[
          {
            '@type': 'ContactPoint',
            telephone: '+48 789 011 336',
            contactType: 'customer support',
            areaServed: 'PL',
            availableLanguage: ['pl', 'en'],
          },
          {
            '@type': 'ContactPoint',
            telephone: '+48 798 874 852',
            contactType: 'sales',
            areaServed: ['PL', 'EU', 'US'],
            availableLanguage: ['en', 'pl'],
          },
        ]}
        email="hello@start-up.house"
      />
      <Header menu={menuData?.menu} />
      {blocks.map((block, index) => (
        <DynamicBlockSelector key={`${block?.__typename}-${index}`} typename={block?.__typename} {...block} />
      ))}
      <Footer menu={menuData?.menu} />
    </>
  );
};

Home.getLayout = (page: ReactElement) => {
  const { metadata } = page.props;
  const { menu } = page.props;

  return (
    <DefaultLayout metadata={metadata} menu={menu}>
      {page}
    </DefaultLayout>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const propsBuilder = new PropsBuilder();

  propsBuilder.withQuery(useGetGlobalQuery, {
    variables: {
      locale,
    },
  });
  propsBuilder.withQuery(useGetHomeQuery, {
    variables: {
      locale,
    },
    getMetadata: (data) => data.home?.data?.attributes?.metadata,
  });
  propsBuilder.withQuery(useGetMenuQuery, {});

  const lang = await serverSideTranslations(locale || DEFAULT_LOCALE, [LOCALES.COMMON, LOCALES.HOME]);

  return {
    props: {
      ...(await propsBuilder.build(lang)),
    },
    revalidate: PAGE_REVALIDATE_TIME,
  };
};

export default Home;
